import React, { useContext, useEffect, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { withTranslation } from "react-i18next";
import { AiOutlineClose } from 'react-icons/ai';
import { BiSolidMagicWand } from 'react-icons/bi';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { LS_AI_CONFIG_KEY, LS_AUTHORITY_KEY, LS_COMPANY_INFO_KEY, LS_UI_ELEMENTS_CONFIG_KEY } from '../../core/constants/common';
import AICopilotConfigService from '../../core/service/AICopilotConfigService';
import ReportAIService from '../../core/service/ReportAIService';
import ReportingUtils from '../../core/utilities/ReportingUtils';
import esg_ai from '../../images/esg_ai1.png';
import AIConfigButton from './AIConfigButton';
import './AITextGenWidget.scss';
import { PreferenceContext } from '../../App';

const AIMaterialityTextGen = ({
	t,
	onHide,
	onTransferToEditor,
	content,
	onClearResult,
	page,
	section
}) => {

	const lsData = JSON.parse(localStorage.getItem(LS_AUTHORITY_KEY));
	const companyInfo = JSON.parse(localStorage.getItem(LS_COMPANY_INFO_KEY));
	const aiConfig = JSON.parse(localStorage.getItem(LS_AI_CONFIG_KEY) ?? null);
	const uiElementsConfig = JSON.parse(localStorage.getItem(LS_UI_ELEMENTS_CONFIG_KEY)) ?? null;

	const [loading, setLoading] = useState(false);
	const [text, setText] = useState('');
	const [wordCount, setWordCount] = useState('auto');
	const [styles, setStyles] = useState([]);
	const [activeStyles, setActiveStyles] = useState(aiConfig?.style ? [aiConfig.style.type] : []);
	const [tones, setTones] = useState([]);
	const [activeTones, setActiveTones] = useState(aiConfig?.tone ? [aiConfig.tone.type] : []);
	const [lastAPICallContext, setLastAPICallContext] = useState('');
	const location = useLocation();

	const companyName = lsData.profile.company.legalName;
	const companyId = lsData.profile.company.id;
	const textArea = useRef();
	const { preference } = useContext(PreferenceContext);

	useEffect(() => {
		fetchAllStyles();
		fetchAllTones();
	}, []);

	useEffect(() => {
		if (!onClearResult) {
			setText('');
		}
	}, [onClearResult, page, section]);

	const generateConfigDependentText = () => {
		let payload = {
			job_id: null,
			reporting_standard: 'MATERIALITY',
			section_name: "DoubleMatReport",
			sub_section_name: content.name,
			company_name: companyName,
			industry: companyInfo?.industry?.name ?? '',
			country: companyInfo?.address?.country?.name ?? '',
			company_id: companyId,
			word_limit: wordCount,
			section_id: "DoubleMatReport" + content.name,
			style_id: activeStyles,
			tone_id: activeTones,
			language: preference.language === 'en' ? 'en-us' : preference.language,
			supplementary_data: [{
				topic: content.name,
				qualitative_metrics: [{
					questions: [{
						question: content.name,
						answers: {
							answer: content.html,
						}
					}]
				}]
			}],
			stream: true
		};
		fetchStreamedData(payload);
	};

	const fetchAllStyles = () => {
		AICopilotConfigService.getStyles().then(res => {
			if (res.status !== 200) return;
			setStyles(res.data ?? []);
		}).catch(err => { });
	};

	const fetchAllTones = () => {
		AICopilotConfigService.getTones().then(res => {
			if (res.status !== 200) return;
			setTones(res.data ?? []);
		}).catch(err => { });
	};

	const fetchStreamedData = async (payload) => {
		try {
			setLoading(true);
			const url = ReportAIService.ReportContentGenStreamedAPIURL;
			setLastAPICallContext({
				api_name: url.substring(url.indexOf('/esg'), url.length),
				api_input: payload
			});
			const response = await fetch(url, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify(payload)
			});

			// Handle the streamed data using a response stream
			const reader = response.body.getReader();
			let result = '';
			if (response.status === 200) {
				while (true) {
					const { done, value } = await reader.read();
					setLoading(false);
					if (done) break;
					result += new TextDecoder('utf-8').decode(value);
					setText(result);
					// Auto scroll
					const area = textArea.current;
					if (area) {
						area.scrollTop = area.scrollHeight;
					}
				}
			} else {
				setLoading(false);
				return errorHandler(response);
			}
		} catch (error) {
			console.error('Error fetching data:', error);
			setLoading(false);
		}
	};

	const copyToClipboard = () => {
		navigator.clipboard.writeText(text);
		toast.info('Copied');
	};

	const errorHandler = (error) => {
		const _ = ReportingUtils.decodeErrorMessage(error?.status, '', t);
		toast.error(
			<div>
				<h6>{_.header}</h6>
				<div>{_.message}</div>
			</div>, {
			position: toast.POSITION.TOP_RIGHT,
			autoClose: false
		});
		return Promise.reject(error);
	};

	const renderAIConfig = () => {
		return (
			<div className='ESGCopilot_Config_section'>
				<div className='card mb-1'>
					<div className='card-body p-1'>

						<div className='Personality_listCard'>
							<div className="Personality_listCard-head">
								<h6>{t('Style.label')}</h6>
							</div>
							<div className="Personality_listCard-body">
								{
									styles?.map((p, pIndex) =>
										<AIConfigButton
											id={`checkbutton-${p.style_id}-${pIndex}`}
											key={`checkbutton-${p.style_id}-${pIndex}`}
											name={p.styleName}
											value={p.type}
											checked={activeStyles?.indexOf(p.type) >= 0}
											classes={
												activeStyles?.indexOf(p.type) >= 0
													? 'outlined-checkbox-btn-success-checked'
													: 'outlined-checkbox-btn-success'
											}
											onCheck={(e) => {
												setActiveStyles([p.type]);
												// Reset selected tones
												setActiveTones([]);
											}}
										/>
									)
								}
							</div>
						</div>

					</div>
				</div>
				<div className='card mb-1'>
					<div className='card-body p-1'>

						<div className='Personality_listCard'>
							<div className="Personality_listCard-head">
								<h6>{t('Tone.label')}</h6>
							</div>
							<div className="Personality_listCard-body">
								{tones?.map((p, pIndex) => renderToneButton(p, pIndex))}
							</div>
						</div>

					</div>
				</div>
				<div className='card'>
					<div className='card-body p-1'>

						<div className='word_lengthCArd'>
							<div className='word_lengthCArd_head'>
								<h6>{t('Length.label')}</h6>
							</div>
							<div className='word_lengthCArd_body'>

								<AIConfigButton
									id='configButtonShort'
									key='configButtonShort'
									name={t('Short.label')}
									value={'150-200'}
									checked={wordCount === '150-200'}
									classes={
										wordCount === '150-200'
											? 'outlined-checkbox-btn-success-checked'
											: 'outlined-checkbox-btn-success'
									}
									toolTip={`150 to 200 words`}
									onCheck={(e) => setWordCount('150-200')}
								/>
								<AIConfigButton
									id='configButtonMedium'
									key='configButtonMedium'
									name={t('Medium.label')}
									value={'200-350'}
									checked={wordCount === '200-350'}
									classes={
										wordCount === '200-350'
											? 'outlined-checkbox-btn-success-checked'
											: 'outlined-checkbox-btn-success'
									}
									toolTip={`200 to 350 words`}
									onCheck={(e) => setWordCount('200-350')}
								/>
								<AIConfigButton
									id='configButtonLong'
									key='configButtonLong'
									name={t('Long.label')}
									value={'350-500'}
									checked={wordCount === '350-500'}
									classes={
										wordCount === '350-500'
											? 'outlined-checkbox-btn-success-checked'
											: 'outlined-checkbox-btn-success'
									}
									toolTip={`300 to 500 words`}
									onCheck={(e) => setWordCount('350-500')}
								/>
								<AIConfigButton
									id='configButtonAuto'
									key='configButtonAuto'
									name={t('Auto.label')}
									value={'auto'}
									checked={wordCount === 'auto'}
									classes={
										wordCount === 'auto'
											? 'outlined-checkbox-btn-success-checked'
											: 'outlined-checkbox-btn-success'
									}
									toolTip={`Count detects automatically`}
									onCheck={(e) => setWordCount('auto')}
								/>
							</div>
						</div>
					</div>
				</div>

				<div className='text-center p-2'>
					<button className='btn btn-ESGAi'
						disabled={loading}
						onClick={() => generateConfigDependentText()}>
						{t('GenerateDraft.label')}
					</button>&nbsp;&nbsp;
					<button className='btn btn-secondary px-3'
						disabled={loading}
						onClick={() => setText('')}>
						{t('Clear.label')}
					</button>
				</div>
				{loading && renderSpinnerLoader()}
			</div>
		);
	};

	const renderToneButton = (p, pIndex) => {
		let isToneDisabled = true;
		for (let index = 0; index < activeStyles.length; index++) {
			const s = activeStyles[index];
			const styleData = styles.find(st => st.type === s);
			if (!styleData) continue;

			if (styleData.compatibleTones?.indexOf(p.type) >= 0) {
				isToneDisabled = false;
				break;
			}
		}
		return (
			<AIConfigButton
				id={`checkbutton-${p.type}-${pIndex}`}
				key={`checkbutton-${p.type}-${pIndex}`}
				name={p.toneName}
				value={p.type}
				checked={activeTones?.indexOf(p.type) >= 0}
				classes={
					activeTones?.indexOf(p.type) >= 0
						? 'outlined-checkbox-btn-success-checked'
						: 'outlined-checkbox-btn-success'
				}
				disabled={isToneDisabled}
				onCheck={(e) => {
					setActiveTones([p.type]);
				}}
			/>
		);
	}

	const renderSpinnerLoader = () => {
		return (
			<div className='text-center'>
				<Spinner animation="grow" variant="warning" />
				<Spinner animation="grow" variant="warning" />
				<Spinner animation="grow" variant="warning" />
			</div>
		);
	};

	return (
		<div id='textGenWidget' className='1'>
			<div className='widget-header text-white'>
				<span className='widget-header_inner'><img src={esg_ai} width={15} height={15} /> {t('ESGAICopilot.label')}</span>
				<span 
					className="cursor" 
					onClick={() => onHide()}>
					<AiOutlineClose />
				</span>
			</div>
			<div className='widget-body px-2 pt-2'>
				<div className='AIwidget-body_in 1'>
					<div className=''>
						{renderAIConfig()}
						{
							!loading &&
							<div className=' Generate_Draft_previewbox'>
								<div className='p-1'>
									<div className='PreviewView_01'>
										<BiSolidMagicWand />
										<h6>{t('Preview.label')}</h6>
									</div>
									<div className='PreviewView_03'>
										<textarea className='form-control' cols='10' rows='15'
											value={text}
											ref={textArea}
											onChange={() => null}>
										</textarea>
									</div>
								</div>
								<div className='px-1'>
									<div className='responsive_qusCard'>
										{
											!loading &&
											<ul className='ESGAI_Clipboard-EditeBox'>
												<li>
													<button className='btn btn-secondary'
														onClick={() => copyToClipboard()}
														disabled={text == '' || text == null}>
														{t('CopyContent.label')}
													</button>
												</li>

												<li>
													<button className='btn btn-secondary'
														onClick={() => onTransferToEditor(text)}
														disabled={text == '' || text == null}>
														{t('TransferToEditor.label')}
													</button>
												</li>
											</ul>
										}
									</div>
								</div>
							</div>
						}
					</div>
				</div>

				<div className='text-center AITextGenWidge_footerBox'>
					<div className='text-muted Info_textGen'>
						<span>{t('AITextGenWidgetFootNote1.label')}</span>
						<div className="Privacy_termsbox">
							<ul>
								<li>
									<a className="" target="_blank" href={uiElementsConfig?.texts?.termsAndConditions ?? "https://ecodrisil.com/terms-of-use"}>
										Terms & Conditions
									</a>
								</li>
								<li>Feedback @ <a href="mailto:copilot@ecodrisil.com">copilot@ecodrisil.com</a>.
								</li>
							</ul>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

export default withTranslation()(AIMaterialityTextGen);
